import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, ChevronLeft, ChevronRight } from "lucide-react";
const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");
type Installment = { key: string; name: string; term: string; enabled?: boolean; sort_order?: number; partial_text?: string; partialText?: string; partial_note?: string; partialNote?: string };
type Banner = { id: number; image_path: string; link_url?: string; memo?: string; enabled?: boolean; sort_order?: number };
const apiPath = (path: string) => /^https?:\/\//i.test(path) ? path : `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
export function InstallmentBenefits() {
  const [cards, setCards] = useState<Installment[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setState("loading");
    fetch(`${API_BASE}/api/installments`, { signal: controller.signal }).then(async res => {
      if (!res.ok) throw new Error("installments");
      const data = await res.json();
      const rows = data.items || data.installments || data.list || data.data || data.cards || [];
      setCards(Array.isArray(rows) ? rows.filter((row: Installment) => row.name && row.key && row.enabled !== false).sort((a: Installment, b: Installment) => (a.sort_order || 0) - (b.sort_order || 0)) : []);
      setState("ready");
    }).catch(() => { if (!controller.signal.aborted) setState("error"); });
    return () => controller.abort();
  }, [retry]);
  return <section className="jp-section jp-installments" aria-labelledby="installments-heading"><div className="jp-wrap">
    <div className="jp-section-heading"><h2 id="installments-heading">카드사별 할부 혜택</h2><CreditCard size={32} strokeWidth={1.4} /></div>
    {state === "loading" ? <p role="status" className="jp-empty">카드 혜택을 불러오고 있어요.</p> : state === "error" ? <div className="jp-empty" role="status">카드 혜택을 불러오지 못했어요. <button className="jp-text-link" onClick={() => setRetry(v => v + 1)}>다시 불러오기</button></div> : cards.length ? <div className="jp-card-benefits">{cards.map(card => <article key={card.key}><h3>{card.name}</h3><p>{card.term && /\d/.test(card.term) && !/개월/.test(card.term) ? `${card.term}개월` : card.term || "조건 확인 필요"}</p>{(card.partial_text || card.partialText || card.partial_note || card.partialNote) && <details><summary>할부 상세 조건</summary>{(card.partial_text || card.partialText) && <p>{card.partial_text || card.partialText}</p>}{(card.partial_note || card.partialNote) && <small>{card.partial_note || card.partialNote}</small>}</details>}</article>)}</div> : <p className="jp-empty">등록된 무이자 할부 안내가 없습니다. 적용 가능한 혜택은 결제창에서 확인해 주세요.</p>}
    <details className="jp-fine-print jp-benefit-notes"><summary>할부 유의사항</summary><ul>
      <li>결제금액 5만원 이상부터 적용됩니다.</li>
      <li>제외카드(체크, 선불, 법인, 기프트 하이브리드, 은행계열 등)는 무이자 할부 적용이 제한될 수 있습니다.</li>
      <li>은행계열카드: 카드 전·후면에 BC로고가 없는 수협, 제주 등 일부 카드는 무이자 적용이 제한될 수 있습니다.</li>
      <li>카드사별 혜택과 부분 무이자 조건을 확인해 주세요. 최종 적용 조건은 카드사 안내와 결제창을 기준으로 합니다.</li>
    </ul></details>
  </div></section>;
}
export function HomeBanners() {
  const nav = useNavigate();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const touchStart = useRef<number | null>(null);
  const swiped = useRef(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API_BASE}/api/banners`, { signal: controller.signal }).then(res => res.ok ? res.json() : null).then(data => {
      const rows = Array.isArray(data?.items) ? data.items : [];
      setBanners(rows.filter((row: Banner) => row.image_path && row.enabled !== false).sort((a: Banner, b: Banner) => (a.sort_order || 0) - (b.sort_order || 0)));
    }).catch(() => {});
    return () => controller.abort();
  }, []);
  useEffect(() => {
    if (banners.length < 2 || paused || hovered || focused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(() => setIndex(v => (v + 1) % banners.length), 5500);
    return () => window.clearInterval(timer);
  }, [banners.length, paused, hovered, focused]);
  if (!banners.length) return null;
  const current = banners[index % banners.length];
  const open = () => {
    if (swiped.current) { swiped.current = false; return; }
    const link = (current.link_url || "").trim();
    if (/^https?:\/\//i.test(link)) window.open(link, "_blank", "noopener,noreferrer");
    else if (link && !/^[a-z][a-z0-9+.-]*:/i.test(link) && !link.startsWith("//")) nav(link.startsWith("/") ? link : `/${link}`);
  };
  return <section className="jp-banners jp-wrap" aria-label="주류페이 소식" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onFocus={() => setFocused(true)} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}
    onTouchStart={event => { touchStart.current = event.touches[0]?.clientX ?? null; swiped.current = false; setPaused(true); }}
    onTouchEnd={event => {
      const end = event.changedTouches[0]?.clientX;
      if (touchStart.current !== null && end !== undefined && Math.abs(end - touchStart.current) > 40 && banners.length > 1) {
        swiped.current = true;
        const direction = end < touchStart.current ? 1 : -1;
        setIndex(value => (value + direction + banners.length) % banners.length);
      }
      touchStart.current = null;
    }}>
    {current.link_url ? <button onClick={open} className="jp-banner-image"><img src={apiPath(current.image_path)} alt={current.memo || "주류페이 소식 자세히 보기"} /></button> : <div className="jp-banner-image"><img src={apiPath(current.image_path)} alt={current.memo || "주류페이 소식"} /></div>}
    {banners.length > 1 && <div className="jp-banner-controls"><button aria-label="이전 소식" onClick={() => setIndex(v => (v - 1 + banners.length) % banners.length)}><ChevronLeft size={18} /></button><span>{index + 1} / {banners.length}</span><button aria-label="다음 소식" onClick={() => setIndex(v => (v + 1) % banners.length)}><ChevronRight size={18} /></button><button onClick={() => setPaused(v => !v)}>{paused ? "자동 넘김 재생" : "자동 넘김 정지"}</button></div>}
  </section>;
}
